import { apiService } from 'core/services/apiService';
import { IHandbookDocument } from 'core/interfaces/handbookInterface';

export type HandbookOwnerType = 'organization' | 'workplace';

export const resolveHandbookWorkplace = (ownerType: HandbookOwnerType): string | null => {
  if (ownerType === 'workplace') {
    const id = localStorage.getItem('workplace-id');
    return id ? `/api/workplaces/${id}` : null;
  }
  return null;
};

export const getHandbookOwnerType = (workplace: string | null | undefined): HandbookOwnerType =>
  workplace ? 'workplace' : 'organization';

export const getHandbookDocumentUrl = (filePath?: string): string | null => {
  if (!filePath) return null;
  const base = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
  const path = filePath.replace(/^\//, '');
  return `${base}/employee_handbook/${path}`;
};

const appendWorkplaceField = (formData: FormData, workplace: string | null) => {
  if (workplace) {
    formData.append('workplace', workplace);
  } else {
    formData.append('workplace', '');
  }
};

// ========================
// Handbook Documents
// ========================

export interface ListHandbookDocumentsOptions {
  isLatestVersion?: boolean;
  throwOnError?: boolean;
}

const buildHandbookDocumentQueryParams = (
  workplaceId?: number,
  options?: ListHandbookDocumentsOptions
): URLSearchParams => {
  const id = workplaceId || Number(localStorage.getItem('workplace-id'));
  const params = new URLSearchParams();
  params.append('pagination', 'false');
  if (id && !Number.isNaN(id)) {
    params.append('workplace', id.toString());
  }
  if (options?.isLatestVersion) {
    params.append('isLatestVersion', 'true');
  }
  params.append('order[createdAt]', 'asc');
  return params;
};

const parseHandbookDocumentsResponse = (data: unknown): IHandbookDocument[] => {
  let items: IHandbookDocument[] = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (data && typeof data === 'object' && 'member' in data) {
    const member = (data as { member: IHandbookDocument | IHandbookDocument[] }).member;
    items = Array.isArray(member) ? member : [member];
  }
  return items.filter((d) => d.active !== false);
};

const fetchHandbookDocuments = async (
  url: string,
  options?: ListHandbookDocumentsOptions
): Promise<IHandbookDocument[]> => {
  try {
    const response = await apiService.get(url);
    const data = apiService.responseHandler(response);
    return parseHandbookDocumentsResponse(data);
  } catch (error) {
    console.error('Error fetching handbook documents:', error);
    if (options?.throwOnError) {
      throw error;
    }
    return [];
  }
};

export const listHandbookDocuments = async (
  workplaceId?: number,
  options?: ListHandbookDocumentsOptions
): Promise<IHandbookDocument[]> => {
  const params = buildHandbookDocumentQueryParams(workplaceId, options);
  return fetchHandbookDocuments(`employee_handbook_documents?${params.toString()}`, options);
};

export const getHandbookDocumentVersions = async (
  documentKey: string,
  workplaceId?: number,
  options?: ListHandbookDocumentsOptions
): Promise<IHandbookDocument[]> => {
  const params = buildHandbookDocumentQueryParams(workplaceId, options);
  return fetchHandbookDocuments(
    `employee-handbook-documents/${documentKey}/versions?${params.toString()}`,
    options
  );
};

export const uploadHandbookDocument = async (
  file: File,
  name: string
): Promise<IHandbookDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name.trim());
  appendWorkplaceField(formData, resolveHandbookWorkplace('workplace'));

  const response = await apiService.postUpload('employee-handbook-documents', formData);
  const data = apiService.responseHandler(response);
  return data;
};

export const replaceHandbookDocument = async (
  id: number,
  file: File,
  name: string
): Promise<IHandbookDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name.trim());

  const response = await apiService.postUpload(`employee-handbook-documents/${id}`, formData);
  const data = apiService.responseHandler(response);
  return data;
};

export const renameHandbookDocument = async (
  id: number,
  name: string
): Promise<IHandbookDocument> => {
  const response = await apiService.patch(`employee_handbook_documents/${id}`, {
    name: name.trim(),
  });
  const data = apiService.responseHandler(response);
  return data;
};

export const deleteHandbookDocument = async (id: number): Promise<void> => {
  await apiService.delete(`employee_handbook_documents/${id}`);
};
